<template>
  <div class="container">
    <div>
      <md-field>
        <md-select
          :value="storeGroup"
          @input="$emit('update:storeGroup', $event)"
        >
          <md-option v-for="c in storeGroups" :key="c.code" :value="c.code">
            {{ c.name }}
          </md-option>
        </md-select>
      </md-field>
    </div>

    <gl-tree-view
      :entries="treeEntries"
      :selected="stores"
      v-on:update:selected="$emit('update:stores', $event)"
    >
      <template v-slot="_">
        <slot :vm="_.vm" :entry="_.entry">
          <div
            :title="
              _.entry.children && _.entry.children.length > 0
                ? ''
                : getDisplayEntryLabel(_.entry)
            "
          >
            {{ getDisplayEntryLabel(_.entry) }}
          </div>
        </slot>
      </template>
    </gl-tree-view>
  </div>
</template>

<script>
import GlTreeView from '@/components/common/GlTreeView.vue'
import ErrorReport from '@/mixins/ErrorReport'
import { collectCodes } from '@/util/list'

/**
 * 店舗（任意複数）選択コンポーネント
 */
export default {
  components: { GlTreeView },
  mixins: [ErrorReport],
  props: {
    /**
     * 選択可能店舗数設定
     */
    storeCountMax: {
      type: Number,
    },
    /**
     * 選択している店舗グループ
     */
    storeGroup: {
      type: String,
    },
    /**
     * 選択している店舗
     */
    stores: {
      type: Array,
      default: () => [],
    },
    /**
     * 店舗を企業権限に関係なしに全部取る
     */
    all: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      /**
       * 店舗グループマスタ
       */
      storeGroups: [],
      /**
       * 店舗ツリー描画用データ
       */
      treeEntries: [],
    }
  },
  watch: {
    storeGroup: {
      immediate: true,
      async handler(group, oldGroup) {
        if (!group) {
          return
        }

        let stores = this.stores

        // 手動変更の場合には店舗選択をクリアする（このコンポーネントを使っているコンポーネントが
        // 店舗クリアを検知できるようemitも行う）。
        // ※ 店舗グループ選択は必須なので、手動で変更する場合、'group1' -> 'group2' となる。
        // ※ 初回にグループを設定する場合には undefined -> 'group1' となる
        if (oldGroup) {
          stores = []
          this.$emit('update:stores', [])
        }

        this.treeEntries = []

        const storesTree = await this.$repositories
          .get('store')
          .get(group, this.all)
          .then(stores => [
            { code: undefined, name: '全店舗', children: stores, open: true },
          ])

        // 店舗グループマスタの変更によって減った場合、それらは選択中のstoresから削除する
        // 手動で変更時（oldGroupがある場合）はスキップ
        if (!oldGroup) {
          const set = collectCodes(storesTree, 'code')
          stores = this.stores.filter(store => set.has(store))
          this.$emit('update:stores', stores)
        }
        if (stores.length > 0) {
          const storesSet = new Set(stores)

          walk(storesTree[0], 0, entry => {
            if (entry.children) {
              return
            }
            entry.state = storesSet.has(entry.code) ? 'checked' : 'none'
          })
        }

        this.treeEntries = storesTree
      },
    },
  },
  computed: {
    errors() {
      const errors = []

      if (!this.storeGroup) {
        errors.push('店舗グループを選択してください')
      }
      if (!this.stores || this.stores.length === 0) {
        errors.push('店舗を選択してください')
      }
      if (this.storeCountMax) {
        // 店舗数制限チェック
        if (this.stores && this.stores.length > this.storeCountMax) {
          errors.push(
            this.stores.length +
              '店舗選択、' +
              this.storeCountMax +
              '店舗までの選択にしてください',
          )
        }
      }
      return errors
    },
  },
  methods: {
    /**
     * 表示ラベル取得
     *
     * 葉の場合のみコードも表示します。
     */
    getDisplayEntryLabel(entry) {
      if (entry.children && entry.children.length > 0) {
        return entry.name
      }

      // コードは `企業コード,店舗コード` のフォーマットなので、店舗コードだけにします
      const storeCode = entry.code
        .split(',')
        .slice(1)
        .join(',')
      return storeCode + '：' + entry.name
    },
  },
  async mounted() {
    this.storeGroups = await this.$repositories.get('storeGroup').get(this.all)

    if (!this.storeGroups || this.storeGroups.length === 0) {
      // 店舗グループが取得できない場合はもう続行不可
      throw new Error('店舗グループが取得できませんでした。')
    }

    if (!this.storeGroup) {
      // 店舗グループの選択は必須なので、初期で指定されていない場合は店舗グループ一覧の
      // 先頭を選んでいる状態で始める
      // こちらで自動設定したいので、Syncしている親コンポーネントに通知の必要があります
      this.$emit('update:storeGroup', this.storeGroups[0].code)
    }
  },
}

/**
 * ツリーノードの再帰処理
 */
function walk(entry, level, f) {
  if (entry.children) {
    for (const child of entry.children) {
      walk(child, level + 1, f)
    }
  }
  f(entry, level)
}
</script>
